<template>
  <ClienteSideDrawer
    :open="open && !!usuario"
    :title="usuario?.nomeCompleto || ''"
    :subtitle="usuario?.email || ''"
    wide
    panel-class="sa-user-tools-panel"
    body-class="sa-user-tools-drawer__body"
    @close="emit('close')"
  >
    <template #icon>
      <div class="sa-user-tools-drawer__avatar" :style="avatarStyle(usuario)">
        {{ userInitials(usuario) }}
      </div>
    </template>

    <div v-if="usuario" class="sa-user-tools-drawer">
      <div class="sa-user-tools-hero">
        <span class="sa-user-tools-chip sa-user-tools-chip--role">{{ usuario.roleNome }}</span>
        <span class="sa-user-tools-chip sa-user-tools-chip--pro">Pro</span>
        <span
          v-if="usuario.acessoBloqueado"
          class="sa-user-tools-chip sa-user-tools-chip--blocked"
        >
          {{ t('accountBlocked.blockedTag') }}
        </span>
        <span v-if="usuario.diaVencimentoCobranca" class="sa-user-tools-chip sa-user-tools-chip--due">
          {{ t('superAdmin.billingDueDayLabel', { day: usuario.diaVencimentoCobranca }) }}
        </span>
      </div>

      <section class="sa-user-tools-card">
        <header class="sa-user-tools-card__head">
          <div class="sa-user-tools-card__icon sa-user-tools-card__icon--shield">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
          </div>
          <div>
            <h3 class="sa-user-tools-card__title">{{ t('superAdmin.userTools.permissionsTitle') }}</h3>
            <p class="sa-user-tools-card__sub">{{ t('superAdmin.userTools.permissionsSub') }}</p>
          </div>
        </header>

        <div class="sa-setting-list">
          <div class="sa-setting-row">
            <div class="sa-setting-row__icon sa-setting-row__icon--green">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
              </svg>
            </div>
            <div class="sa-setting-row__body">
              <span class="sa-setting-row__title">{{ t('accountBlocked.accessLabel') }}</span>
              <span class="sa-setting-row__desc">{{ t('superAdmin.userTools.accessDesc') }}</span>
            </div>
            <label class="sa-toggle" :title="usuario.acessoBloqueado ? t('accountBlocked.accessBlocked') : t('accountBlocked.accessActive')">
              <input
                type="checkbox"
                class="sa-toggle__input"
                :checked="!usuario.acessoBloqueado"
                @change="emit('toggle-access', $event.target.checked)"
              />
              <span class="sa-toggle__track"><span class="sa-toggle__thumb" /></span>
            </label>
          </div>

          <div class="sa-setting-row">
            <div class="sa-setting-row__icon sa-setting-row__icon--indigo">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
              </svg>
            </div>
            <div class="sa-setting-row__body">
              <span class="sa-setting-row__title">{{ t('superAdmin.colPodeCriarWorkspace') }}</span>
              <span class="sa-setting-row__desc">{{ t('superAdmin.userTools.createWsDesc') }}</span>
            </div>
            <label
              class="sa-toggle"
              :title="usuario.podeCriarWorkspace ? t('superAdmin.podeCriarWorkspaceActive') : t('superAdmin.podeCriarWorkspaceInactive')"
            >
              <input
                type="checkbox"
                class="sa-toggle__input"
                :checked="Boolean(usuario.podeCriarWorkspace)"
                @change="emit('toggle-create-ws', $event.target.checked)"
              />
              <span class="sa-toggle__track"><span class="sa-toggle__thumb" /></span>
            </label>
          </div>
        </div>

        <div class="sa-setting-status">
          <span
            class="sa-status-pill"
            :class="usuario.acessoBloqueado ? 'sa-status-pill--off' : 'sa-status-pill--on'"
          >
            {{ usuario.acessoBloqueado ? t('accountBlocked.accessBlocked') : t('accountBlocked.accessActive') }}
          </span>
          <span
            class="sa-status-pill"
            :class="usuario.podeCriarWorkspace ? 'sa-status-pill--on' : 'sa-status-pill--off'"
          >
            {{ usuario.podeCriarWorkspace ? t('superAdmin.podeCriarWorkspaceActive') : t('superAdmin.podeCriarWorkspaceInactive') }}
          </span>
        </div>
      </section>

      <section class="sa-user-tools-card">
        <header class="sa-user-tools-card__head">
          <div class="sa-user-tools-card__icon sa-user-tools-card__icon--amber">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
            </svg>
          </div>
          <div>
            <h3 class="sa-user-tools-card__title">{{ t('superAdmin.panelLimits') }}</h3>
            <p class="sa-user-tools-card__sub">{{ t('superAdmin.billingDueDayHint') }}</p>
          </div>
        </header>

        <div class="sa-limits-grid sa-limits-grid--valor">
          <div class="sa-limit-card sa-limit-card--full">
            <label class="sa-limit-card__label">{{ t('superAdmin.billing.valor') }}</label>
            <div class="sa-limit-card__control sa-limit-card__control--valor">
              <div class="sa-valor-mask-wrap">
                <span class="sa-valor-mask-prefix">R$</span>
                <input
                  :value="draftValorMensalDisplay"
                  type="text"
                  inputmode="decimal"
                  class="sa-limit-card__input sa-limit-card__input--valor"
                  :placeholder="t('superAdmin.billing.valorPlaceholder')"
                  @input="onValorMensalInput"
                />
              </div>
              <button type="button" class="sa-limit-card__btn" @click="emit('apply-valor-mensal')">
                {{ t('superAdmin.apply') }}
              </button>
            </div>
          </div>
        </div>

        <div class="sa-limits-grid">
          <div class="sa-limit-card">
            <label class="sa-limit-card__label">{{ t('superAdmin.colMaxWorkspaces') }}</label>
            <div class="sa-limit-card__control">
              <input
                :value="draftMaxWs"
                class="sa-limit-card__input"
                type="number"
                min="0"
                :placeholder="t('superAdmin.maxWorkspacesPlaceholder')"
                @input="emit('update:draftMaxWs', $event.target.value === '' ? undefined : Number($event.target.value))"
              />
              <button type="button" class="sa-limit-card__btn" @click="emit('apply-max-ws')">
                {{ t('superAdmin.apply') }}
              </button>
            </div>
          </div>

          <div class="sa-limit-card">
            <label class="sa-limit-card__label">{{ t('superAdmin.billingDueDay') }}</label>
            <div class="sa-limit-card__control">
              <input
                :value="draftDiaVenc"
                class="sa-limit-card__input sa-limit-card__input--day"
                type="number"
                min="1"
                max="31"
                :placeholder="t('superAdmin.billingDueDayPlaceholder')"
                @input="emit('update:draftDiaVenc', $event.target.value === '' ? undefined : Number($event.target.value))"
              />
              <button type="button" class="sa-limit-card__btn sa-limit-card__btn--muted" @click="emit('apply-dia-venc')">
                {{ t('superAdmin.apply') }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="sa-user-tools-card">
        <header class="sa-user-tools-card__head">
          <div class="sa-user-tools-card__icon sa-user-tools-card__icon--violet">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
            </svg>
          </div>
          <div>
            <h3 class="sa-user-tools-card__title">{{ t('superAdmin.estruturacao.title') }}</h3>
            <p class="sa-user-tools-card__sub">
              {{ isAdminRole ? t('superAdmin.userTools.estrutAdminNote') : t('superAdmin.userTools.estrutSub') }}
            </p>
          </div>
        </header>

        <div class="sa-estrut-grid">
          <label
            v-for="section in ESTRUTURACAO_SECTIONS"
            :key="section.screenId"
            class="sa-estrut-card"
            :class="{
              'sa-estrut-card--on': hasEstruturacao(section.screenId),
              'sa-estrut-card--disabled': isAdminRole
            }"
            :title="t(`superAdmin.estruturacao.${section.screenId}`)"
          >
            <input
              type="checkbox"
              class="sa-estrut-card__input"
              :checked="hasEstruturacao(section.screenId)"
              :disabled="isAdminRole"
              @change="emit('toggle-estrut', section.screenId, $event.target.checked)"
            />
            <div
              class="sa-estrut-card__icon"
              :style="{ background: `linear-gradient(135deg, ${section.accentFrom}, ${section.accentTo})` }"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="section.iconPath"/>
              </svg>
            </div>
            <span class="sa-estrut-card__name">{{ t(`superAdmin.estruturacao.${section.screenId}`) }}</span>
            <span class="sa-estrut-card__badge">{{ section.badge }}</span>
            <span class="sa-estrut-card__toggle" aria-hidden="true">
              <span class="sa-estrut-card__toggle-thumb" />
            </span>
          </label>
        </div>
      </section>
    </div>
  </ClienteSideDrawer>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatCurrencyMask, parseCurrencyMask } from '@/utils/currencyMask'
import ClienteSideDrawer from '../cliente/ClienteSideDrawer.vue'
import { ESTRUTURACAO_SECTIONS } from '../../constants/estruturacaoSections'

const emit = defineEmits([
  'close',
  'toggle-access',
  'toggle-create-ws',
  'apply-max-ws',
  'apply-dia-venc',
  'toggle-estrut',
  'update:draftMaxWs',
  'update:draftDiaVenc',
  'update:draftValorMensal',
  'apply-valor-mensal'
])

const props = defineProps({
  open: { type: Boolean, default: false },
  usuario: { type: Object, default: null },
  draftMaxWs: { type: [Number, String], default: undefined },
  draftDiaVenc: { type: [Number, String], default: undefined },
  draftValorMensal: { type: [Number, String], default: undefined },
  isAdminRole: { type: Boolean, default: false },
  hasEstruturacao: { type: Function, required: true }
})

const { t, locale } = useI18n()

const draftValorMensalDisplay = computed(() => {
  const v = Number(props.draftValorMensal)
  return v > 0 ? formatCurrencyMask(v, locale.value) : ''
})

function onValorMensalInput (event) {
  const parsed = parseCurrencyMask(event?.target?.value ?? '')
  emit('update:draftValorMensal', parsed > 0 ? parsed : null)
}

function userInitials (usuario) {
  const name = String(usuario?.nomeCompleto || usuario?.email || '?').trim()
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

function avatarStyle (usuario) {
  const seed = String(usuario?.email || usuario?.nomeCompleto || usuario?.id || 'user')
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash)
  const hue = Math.abs(hash) % 360
  return { background: `linear-gradient(135deg, hsl(${hue} 62% 48%), hsl(${(hue + 36) % 360} 58% 40%))` }
}
</script>

<style scoped>
:deep(.sa-user-tools-panel .cliente-side-drawer-header) {
  background: linear-gradient(135deg, #4338ca 0%, #7c3aed 100%);
  border-bottom: none;
}

:deep(.sa-user-tools-panel .cliente-side-drawer-header__name) {
  color: #fff;
}

:deep(.sa-user-tools-panel .cliente-side-drawer-header__sub) {
  color: rgba(255, 255, 255, 0.82);
}

:deep(.sa-user-tools-panel .cliente-side-drawer-close) {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  border-radius: 4px;
}

:deep(.sa-user-tools-panel .cliente-side-drawer-close:hover) {
  background: rgba(255, 255, 255, 0.24);
  color: #fff;
}

:deep(.sa-user-tools-panel .cliente-side-drawer-header__icon) {
  width: auto;
  height: auto;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
}

:deep(.sa-user-tools-drawer__body) {
  padding: 0;
  background: var(--bg-primary, #f8fafc);
}

.sa-user-tools-drawer__avatar {
  width: 46px;
  height: 46px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

.sa-user-tools-drawer {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 18px 24px;
}

.sa-user-tools-hero {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.sa-user-tools-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.sa-user-tools-chip--role {
  color: #4338ca;
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.22);
}

.sa-user-tools-chip--pro {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
  border: 1px solid rgba(124, 58, 237, 0.2);
}

.sa-user-tools-chip--blocked {
  color: #dc2626;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.22);
}

.sa-user-tools-chip--due {
  color: #b45309;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.22);
}

.sa-user-tools-card {
  border: 1px solid var(--border-primary, #e5e7eb);
  border-radius: 4px;
  background: var(--bg-secondary, #fff);
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sa-user-tools-card__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid color-mix(in srgb, var(--border-primary) 70%, transparent);
  background: color-mix(in srgb, var(--bg-primary) 40%, var(--bg-secondary));
}

.sa-user-tools-card__icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sa-user-tools-card__icon svg {
  width: 18px;
  height: 18px;
}

.sa-user-tools-card__icon--shield {
  color: #4338ca;
  background: rgba(99, 102, 241, 0.12);
}

.sa-user-tools-card__icon--amber {
  color: #b45309;
  background: rgba(245, 158, 11, 0.12);
}

.sa-user-tools-card__icon--violet {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.12);
}

.sa-user-tools-card__title {
  margin: 0 0 3px;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.sa-user-tools-card__sub {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-secondary);
}

.sa-setting-list {
  display: flex;
  flex-direction: column;
}

.sa-setting-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid color-mix(in srgb, var(--border-primary) 55%, transparent);
}

.sa-setting-row:last-child {
  border-bottom: none;
}

.sa-setting-row__icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sa-setting-row__icon svg {
  width: 17px;
  height: 17px;
}

.sa-setting-row__icon--green {
  color: #15803d;
  background: rgba(34, 197, 94, 0.12);
}

.sa-setting-row__icon--indigo {
  color: #4338ca;
  background: rgba(99, 102, 241, 0.12);
}

.sa-setting-row__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sa-setting-row__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.sa-setting-row__desc {
  font-size: 11px;
  line-height: 1.4;
  color: var(--text-secondary);
}

.sa-toggle {
  flex-shrink: 0;
  cursor: pointer;
}

.sa-toggle__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.sa-toggle__track {
  display: block;
  position: relative;
  width: 46px;
  height: 26px;
  border-radius: 999px;
  background: #22c55e;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
  transition: background 0.2s ease;
}

.sa-toggle__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: transform 0.2s ease;
}

.sa-toggle__input:not(:checked) + .sa-toggle__track {
  background: #ef4444;
}

.sa-toggle__input:not(:checked) + .sa-toggle__track .sa-toggle__thumb {
  transform: translateX(20px);
}

.sa-setting-status {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 16px 14px;
  background: color-mix(in srgb, var(--bg-primary) 50%, transparent);
}

.sa-status-pill {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
}

.sa-status-pill--on {
  color: #15803d;
  background: rgba(34, 197, 94, 0.12);
}

.sa-status-pill--off {
  color: #dc2626;
  background: rgba(239, 68, 68, 0.1);
}

.sa-limits-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 14px 16px 16px;
}

.sa-limit-card {
  padding: 12px;
  border-radius: 4px;
  border: 1px solid color-mix(in srgb, var(--border-primary) 75%, transparent);
  background: var(--bg-primary);
}

.sa-limit-card__label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

.sa-limit-card__control {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sa-limit-card__input {
  width: 100%;
  padding: 9px 10px;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.sa-limit-card__input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.12);
}

.sa-limit-card__btn {
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  color: #fff;
}

.sa-limit-card__btn--muted {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  color: var(--text-primary);
}

.sa-limits-grid--valor {
  padding-bottom: 0;
}

.sa-limit-card--full {
  grid-column: 1 / -1;
}

.sa-limit-card__control--valor {
  flex-direction: row;
  align-items: stretch;
}

.sa-valor-mask-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  background: var(--bg-secondary);
}

.sa-valor-mask-wrap:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.12);
}

.sa-valor-mask-prefix {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-secondary);
}

.sa-limit-card__input--valor {
  border: none;
  padding: 9px 0;
  background: transparent;
  box-shadow: none;
}

.sa-limit-card__input--valor:focus {
  border: none;
  box-shadow: none;
}

.sa-limit-card__control--valor .sa-limit-card__btn {
  width: auto;
  min-width: 88px;
  flex-shrink: 0;
}

.sa-estrut-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 14px 16px 16px;
}

.sa-estrut-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid color-mix(in srgb, var(--border-primary) 75%, transparent);
  background: var(--bg-primary);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.sa-estrut-card:hover:not(.sa-estrut-card--disabled) {
  border-color: color-mix(in srgb, #6366f1 40%, var(--border-primary));
}

.sa-estrut-card--on {
  border-color: color-mix(in srgb, #22c55e 45%, var(--border-primary));
  box-shadow: 0 0 0 1px color-mix(in srgb, #22c55e 20%, transparent);
}

.sa-estrut-card--disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.sa-estrut-card__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.sa-estrut-card__icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.sa-estrut-card__icon svg {
  width: 16px;
  height: 16px;
}

.sa-estrut-card__name {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
}

.sa-estrut-card__badge {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 10px;
  font-weight: 800;
  color: var(--text-tertiary, var(--text-secondary));
  opacity: 0.55;
}

.sa-estrut-card__toggle {
  align-self: flex-end;
  position: relative;
  width: 38px;
  height: 22px;
  border-radius: 999px;
  background: #e5e7eb;
  transition: background 0.2s ease;
}

.sa-estrut-card--on .sa-estrut-card__toggle {
  background: #22c55e;
}

.sa-estrut-card__toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

.sa-estrut-card--on .sa-estrut-card__toggle-thumb {
  transform: translateX(16px);
}

@media (max-width: 560px) {
  .sa-limits-grid,
  .sa-estrut-grid {
    grid-template-columns: 1fr;
  }
}
</style>
